import Debug from "../../../../common-deprecated/Debug";
import type {
    LegalTextType,
    CCGetMonthlyPaymentResponseType,
    ParsedMonthlyRateType,
    QuoteInfoType,
} from "../../../../common-deprecated/types/FinanceInsuranceRateTypes";
import { sanitize } from "../../../../common-deprecated/utils/sanitizer";

import type { LegalTextResponseType } from "../../types/FinanceTypes";

// Helper function to get values from getMonthlyPayment response.
export const calcFind = (
    calculations: { Name: string; Value: string; Formatted: string }[],
    name: string,
    useFormat: boolean = false,
): string => {
    const foundValue = calculations.find((calc) => calc.Name.toLowerCase() === name.toLowerCase());
    return foundValue ? foundValue[useFormat ? "Formatted" : "Value"] : "";
};

export const formatCarFinance = (
    carMonthlyRate: CCGetMonthlyPaymentResponseType,
): ParsedMonthlyRateType | undefined => {
    if (!carMonthlyRate.Quotation || !carMonthlyRate.Quotation.Calculations) {
        Debug.warn("No calculations found in monthly rate response");
    } else {
        const promoCalculations = carMonthlyRate.Quotation.Calculations.PromoCalculation;
        const calculations = carMonthlyRate.Quotation.Calculations.Calculation;
        // Try to get values from getMonthlyPayment.
        // We're using the T1 service rate key mapping here to stay consistent with the implementation in frontend.
        // Ideally the frontend only receives T1 service rate keys.
        // See confluence for additional info: https://marlon.atlassian.net/wiki/spaces/TORD/pages/2797093/Flexibility+matrix+and+T1+Finance+service#FlexibilitymatrixandT1Financeservice-Propertytablemap
        const actualCalculations = promoCalculations || calculations;
        const calculationObject: ParsedMonthlyRateType = {
            total: calcFind(actualCalculations, "MONTHLY_PAYMENT"),
            // This is a value filled in from the regular calculations
            totalWithoutPromotions: calcFind(calculations, "MONTHLY_PAYMENT"),
            info: {
                term: calcFind(actualCalculations, "TERM"),
                productName: calcFind(actualCalculations, "PRODUCT_NAME"),
                productType: calcFind(actualCalculations, "PRODUCT_TYPE"),
                effectiveInterestRate: calcFind(actualCalculations, "EFFECTIVE_INTEREST_RATE", true),
                annualInterestRate: calcFind(actualCalculations, "ANNUAL_INTEREST_RATE", true),
                msgCode: calcFind(actualCalculations, "MSG_CODE"),
                mileage: calcFind(actualCalculations, "MILEAGE", true),
                downPayment: calcFind(actualCalculations, "DOWNPAYMENT"),
                residualValue: calcFind(actualCalculations, "RESIDUAL_VALUE"),
                duration: calcFind(actualCalculations, "DURATION"),
                interestRate: calcFind(actualCalculations, "INTEREST_RATE", true),
                totalAmountFinanced: calcFind(actualCalculations, "TOTAL_AMOUNT_FINANCED"),
                amountFinanced: calcFind(actualCalculations, "AMOUNT_FINANCED"),
                totalCreditCostWithFee: calcFind(actualCalculations, "TOTAL_CREDIT_COST_WITH_FEE"),
                installments: calcFind(actualCalculations, "INSTALLMENTS"),
            },
            selectedItemPrices: [],
            unselectedItemPrices: [],
        };

        // These reside under the info object, since this is being passed as is untill it's used in the (priceUtils) formatMonthlyPrice function.

        const quoteInfo: QuoteInfoType = {};
        actualCalculations.forEach((calc) => {
            quoteInfo[calc.Name] = { value: calc.Value, formatted: calc.Formatted };
        });

        calculationObject.quoteInfo = quoteInfo;

        return calculationObject;
    }
};

/**
 * Formats the response from CCGetLegalText
 * @param legalTextResponse
 */
export const formatLegalText = (legalTextResponse: LegalTextResponseType): LegalTextType | null => {
    if (legalTextResponse) {
        const legalText: LegalTextType = {
            // If there is a promotion legal text available, take that one over the regular one
            value: sanitize(
                legalTextResponse?.Legal_Text?.Promo_Legal_Text_Value ||
                    legalTextResponse?.Legal_Text?.Legal_Text_Value,
            ),
        };
        if (legalTextResponse.Legal_Text.Calculations) {
            legalText.table = legalTextResponse.Legal_Text.Calculations.Calculation.map((item) => ({
                name: item.Calculation_Name,
                value: item.Calculation_Value ? sanitize(item.Calculation_Value) : "",
                tooltip: item.Tooltip ? sanitize(item.Tooltip) : "",
            }));
        }
        return legalText;
    }
    return null;
};

/**
 * formats the monthly rates to the right item
 * @param ratesObject
 * @param ratesObjectItemProperty
 */
export const processItemRates = <
    T extends { Error?: string; Quotation: Record<string, Array<{ Id: string; Value: string }>> },
>(
    ratesObject: T,
    ratesObjectItemProperty: string,
): { id: string; value: string }[] => {
    if (ratesObject) {
        if (ratesObject.Error || !ratesObject.Quotation) {
            Debug.error(String(ratesObject.Error), "No finance quotes provided");
        } else {
            // Get item prices from equipmentMonthlyRates response.
            return ratesObject.Quotation[ratesObjectItemProperty].map((item) => ({
                id: item.Id,
                value: item.Value,
            }));
        }
    }
    return [];
};
